import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { validateInvite, acceptInvite, InviteValidation } from '../api/leagues';
import '../styles/global.css';

const JoinLeague: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, login, register, loading: authLoading, refreshUser } = useAuth();

  const [invite, setInvite] = useState<InviteValidation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Auth form state
  const [isRegistering, setIsRegistering] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [grade, setGrade] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    const fetchInvite = async () => {
      if (!token) {
        setError('Invalid invite link');
        setLoading(false);
        return;
      }

      try {
        const data = await validateInvite(token);
        setInvite(data);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Invite not found or expired');
      } finally {
        setLoading(false);
      }
    };
    fetchInvite();
  }, [token]);

  // Auto-accept if user is already logged in
  useEffect(() => {
    const autoAccept = async () => {
      if (user && invite && invite.status === 'pending' && !accepting && !success) {
        if (user.league_id) {
          setError('You are already in a league. Players can only belong to one league.');
          return;
        }
        setAccepting(true);
        try {
          const result = await acceptInvite(token!);
          setSuccess(`Welcome to ${result.league_name}!`);
          await refreshUser();
          setTimeout(() => navigate('/account'), 2000);
        } catch (err: any) {
          setError(err.response?.data?.detail || 'Failed to accept invite');
        } finally {
          setAccepting(false);
        }
      }
    };
    autoAccept();
  }, [user, invite, token, accepting, success, navigate, refreshUser]);

  const handleAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (isRegistering && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (username.length < 3 || username.length > 20) {
      setError('Username must be 3-20 characters');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (isRegistering && grade === null) {
      setError('Please select your grade');
      return;
    }

    setIsSubmitting(true);
    try {
      if (isRegistering) {
        await register(username, password, grade!);
      } else {
        await login(username, password);
      }
      // The useEffect will auto-accept the invite after login
    } catch (err: any) {
      const message = err.response?.data?.detail || 'An error occurred';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || authLoading) {
    return <div className="loading">Loading...</div>;
  }

  if (accepting) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center' }}>
          <h2>Joining League...</h2>
          <p>Please wait...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center' }}>
          <h2>🎉 {success}</h2>
          <p>Redirecting to your account...</p>
        </div>
      </div>
    );
  }

  if (!invite || invite.status !== 'pending') {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center' }}>
          <h2>Invalid Invite</h2>
          <p className="error">{error || 'This invite is no longer valid.'}</p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/')}
            style={{ marginTop: '20px' }}
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  // If user is logged in but already has a league
  if (user && user.league_id) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center' }}>
          <h2>Already in a League</h2>
          <p>You are already a member of a league. Players can only belong to one league at a time.</p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/account')}
            style={{ marginTop: '20px' }}
          >
            Go to My Account
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <h1 className="title">🎉 You're Invited!</h1>
        <h2 className="subtitle">Join {invite.league_name}</h2>
        <p className="description" style={{ marginBottom: '20px' }}>
          {user ? 'Click below to join the league!' : 'Create an account or login to join'}
        </p>

        {error && <div className="error" style={{ marginBottom: '15px' }}>{error}</div>}

        {!user && (
          <>
            <form onSubmit={handleAuthSubmit} className="form">
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                  disabled={isSubmitting}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  disabled={isSubmitting}
                  required
                />
              </div>

              {isRegistering && (
                <>
                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm Password</label>
                    <input
                      type="password"
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm password"
                      disabled={isSubmitting}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="grade">Grade</label>
                    <select
                      id="grade"
                      value={grade ?? ''}
                      onChange={(e) => setGrade(e.target.value ? parseInt(e.target.value) : null)}
                      disabled={isSubmitting}
                      required
                    >
                      <option value="">Select grade</option>
                      <option value="0">Kindergarten</option>
                      <option value="1">1st Grade</option>
                      <option value="2">2nd Grade</option>
                      <option value="3">3rd Grade</option>
                      <option value="4">4th Grade</option>
                      <option value="5">5th Grade</option>
                    </select>
                  </div>
                </>
              )}

              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Please wait...' : (isRegistering ? 'Create Account & Join' : 'Login & Join')}
              </button>
            </form>

            <div className="toggle-link">
              <button
                type="button"
                onClick={() => {
                  setIsRegistering(!isRegistering);
                  setError('');
                }}
                className="link-btn"
              >
                {isRegistering ? 'Already have an account? Login' : 'Create Account'}
              </button>
            </div>
          </>
        )}

        <p style={{ marginTop: '20px', fontSize: '12px', color: '#999' }}>
          Invite expires: {new Date(invite.expires_at).toLocaleString()}
        </p>
      </div>
    </div>
  );
};

export default JoinLeague;
